import { useCallback } from "react";
import { useRecoilState } from "recoil";
import { Box } from "rebass/styled-components";
import { Flex } from "rebass/styled-components";
import styled from "styled-components";

import {
  PlayerRank,
  PlayerName,
  PlayerInfo,
} from "../../Top5Widget/styledComponents";

import { ITopPlayer, ModalType } from "MVVM/@types";
import { analytics } from "MVVM/Analytics";
import { Avatar } from "MVVM/Components/AvatarV2";
import { PlayerFollowButton } from "MVVM/Components/Top5Widget/PlayersList";
import { useMutateFollowers } from "MVVM/Hooks/useMutateFollowers";
import { useUser } from "MVVM/Hooks/useUser";
import { AllstarModalState } from "MVVM/State/modals";

interface ITop5PlayersListProps {
  topPlayers: ITopPlayer[];
}

export const Top5PlayersList = ({ topPlayers }: ITop5PlayersListProps) => {
  const { mutateFollowers } = useMutateFollowers();
  const { allstarUser } = useUser();
  const [allstarModalState, setAllstarModalState] =
    useRecoilState(AllstarModalState);

  const setFollowModal = useCallback(
    ({ displayName, avatar }: { displayName: string; avatar: string }) => {
      setAllstarModalState({
        ...allstarModalState,
        isOpen: ModalType.Signup,
        data: {
          target: displayName,
          avatar: avatar,
          action: "follow",
        },
      });
    },
    [allstarModalState, setAllstarModalState],
  );

  const handleFollow = useCallback(
    async (userId: string, player: ITopPlayer) => {
      if (!allstarUser?.loggedIn) {
        return setFollowModal({
          displayName: player.username,
          avatar: player.avatar || "",
        });
      }

      await mutateFollowers({
        variables: { userToFollow: userId, follow: true },
      });
      analytics.track("User Followed", {
        location: "top_5_widget",
        source: "players_list",
      });
    },
    [mutateFollowers, allstarUser?.loggedIn, setFollowModal],
  );

  const handleUnfollow = useCallback(
    async (userId: string) => {
      await mutateFollowers({
        variables: { userToFollow: userId, follow: false },
      });
      analytics.track("User Unfollowed", {
        location: "top_5_widget",
        source: "players_list",
      });
    },
    [mutateFollowers],
  );
  return (
    <StyledContainer>
      {topPlayers.map((player, index) => (
        <Flex style={{ justifyContent: "space-between", alignItems: "center" }}>
          <Flex
            key={player.internalId}
            style={{ padding: 0, alignItems: "center", gap: "8px" }}
          >
            <PlayerRank
              minWidth={"auto !important"}
              rank={index + 1}
              numberSize="14px"
              lineHeight={"16px"}
            >
              {index + 1}
            </PlayerRank>
            <Box
              onClick={() =>
                (window.location.href = `/u/${
                  player.username || player.internalId
                }`)
              }
              style={{ cursor: "pointer" }}
            >
              <AvatarWrapper>
                <Avatar
                  src={player.avatar}
                  size="xs"
                  userGroup={player.userGroup?.type}
                  rank={player.rank}
                  profileCardUsername={player.username}
                />
              </AvatarWrapper>
            </Box>
            <PlayerInfo>
              <PlayerName
                onClick={() =>
                  (window.location.href = `/u/${
                    player.username || player.internalId
                  }`)
                }
                style={{ cursor: "pointer" }}
              >
                {player.username}
              </PlayerName>
            </PlayerInfo>
          </Flex>
          <StyledFollowButton>
            <PlayerFollowButton
              player={player}
              handleFollow={handleFollow}
              handleUnfollow={handleUnfollow}
            />
          </StyledFollowButton>
        </Flex>
      ))}
    </StyledContainer>
  );
};

export const StyledFollowButton = styled(Box)`
  button {
    border-radius: 20px !important;
    font-size: 10px !important;
    height: 20px !important;
    min-width: 40px !important;
    padding: 0 6px !important;

    > svg {
      display: none !important;
    }

    gap: 0 !important;
  }
`;

const StyledContainer = styled(Flex)`
  backdrop-filter: blur(8px);
  background: rgba(30, 46, 58, 0.4);
  border-radius: 8px;
  border: solid 1px ${({ theme }) => theme.colors.navy50};
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
  padding: 12px;
  width: 100%;

  @media (max-width: 768px) {
    margin-bottom: 0;
  }

  @media (max-width: 1320px) {
    display: none;
  }
`;

const AvatarWrapper = styled(Box)`
  height: 24px;
  width: 24px;

  * {
    border: none !important;
    border-radius: 50% !important;
    height: 100% !important;
    width: 100% !important;
  }
`;
